<template>
    <AthTable v-if="!useAgTable"
        :pubkey="pubkey"
        :isLoading="isLoading"
        :lockFirstColumn="lockFirstColumn"
        :uiInteractive="uiInteractive"
        :fields="fields"
        :superHeaders="superHeaders"
        :data="data"
        :pinnedData="pinnedData"
        :allowRedirect="allowRedirect"
        :emptyProps="emptyProps"
        :restrictHeight="restrictHeight"
        @onSortSelected="onSortSelected"
        @onDropdownItemClicked="onDropdownItemClicked"
        @onCheckboxChange="onCheckboxChange"
        @onButtonClicked="onButtonClicked"
        @onClick="onClick"
        @onClickColumn="onClickColumn"
        @onRedirect="onRedirect" />
    <AgGridTable v-else
        :engine="tableEngine"
        :rows="data"
        :pinnedRows="pinnedData"
        style="height: 40rem" />
</template>
<script>
import AthTable from '../table/Table.vue';
import AgGridTable from './AgGridTable.vue';

export default {
    name: 'mead-ag-grid-conversion-table',
    components: {
        AthTable,
        AgGridTable
    },
    props: {
        /**
         * Whether to render the underlying table using AgGridTable (ag-grid) instead
         * of the legacy AthTable component.
         */
        useAgTable: {type: Boolean, default: false},

        /**
         * Unique key identifying this table instance, passed through to AthTable
         * for its own internal use (e.g. sorting/dropdown event scoping).
         */
        pubkey: {type: [Boolean, String], default: () => false},
        /**
         * Whether the table is in a loading state, passed through to AthTable.
         */
        isLoading: {type: Boolean, default: () => false},
        /**
         * Whether the first column stays locked/pinned in place while the rest of
         * the table scrolls horizontally, passed through to AthTable.
         */
        lockFirstColumn: {type: Boolean, default: () => false},
        /**
         * Whether the table's dropdown/action controls are interactive, passed
         * through to AthTable.
         */
        uiInteractive: {type: Boolean, default: () => true},
        /**
         * Column field definitions describing the table's structure, passed to
         * AthTable and used to build the ag-grid engine via TableEngineConverter.
         */
        fields: {type: Array, default: () => []},
        /**
         * Super header (grouped header row) definitions layered above the regular
         * column headers, passed to AthTable and used to build the ag-grid engine.
         */
        superHeaders: {type: Array, default: () => []},
        /**
         * The row data to render in the table body, passed through to whichever
         * underlying table implementation (AthTable or AgGridTable) is active.
         */
        data: {type: Array, default: () => []},
        /**
         * Row data that stays pinned in place (e.g. totals) while the rest of the
         * table scrolls, passed through to whichever underlying table implementation is active.
         */
        pinnedData: {type: Array, default: () => []},
        /**
         * Whether clicking a row/cell can trigger a redirect, passed through to AthTable.
         */
        allowRedirect: {type: Boolean, default: () => false},
        /**
         * Props controlling the empty-state display shown when there is no data,
         * passed through to AthTable.
         */
        emptyProps: {type: Object, default: () => {}},
        /**
         * Whether the table's height is restricted/capped rather than growing to
         * fit all rows, passed through to AthTable.
         */
        restrictHeight: {type: Boolean, default: () => true},
    },
    methods: {
        onSortSelected: function (value) {
            this.$emit('onSortSelected', value);
        },
        onDropdownItemClicked: function (value) {
            this.$emit('onDropdownItemClicked', value);
        },
        onCheckboxChange: function (value) {
            this.$emit('onCheckboxChange', value);
        },
        onButtonClicked: function (value) {
            this.$emit('onButtonClicked', value);
        },
        onClick: function (value) {
            this.$emit('onClick', value);
        },
        onClickColumn: function (value) {
            this.$emit('onClickColumn', value);
        },
        onRedirect: function (value) {
            this.$emit('onRedirect', value);
        },
    },
    computed: {
        tableEngine: function () {
            const engine = MeadCommon.TableEngineConverter.convert(this.fields, this.superHeaders);
            engine.setRowClickedFunc((event) => {
                this.$emit('onClick', event.data);
            });
            return engine;
        }
    }
}

</script>